import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FaUndoAlt, FaCheckCircle, FaTimesCircle, FaSearch, FaChevronRight,
    FaTruck, FaBoxOpen, FaClipboardCheck, FaMoneyBillWave, FaClock,
    FaEye, FaFilter
} from 'react-icons/fa';
import { HiOutlineRefresh } from 'react-icons/hi';
import { MdLocalShipping } from 'react-icons/md';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import { getReturnRequests, updateReturnStatus } from '../../services/ClientService';

// Return status configuration
const RETURN_STATUS_CONFIG = {
    'Requested': { color: 'bg-blue-100 text-blue-700', icon: <FaClock /> },
    'Approved': { color: 'bg-green-100 text-green-700', icon: <FaCheckCircle /> },
    'Pickup Scheduled': { color: 'bg-purple-100 text-purple-700', icon: <FaTruck /> },
    'Picked Up': { color: 'bg-indigo-100 text-indigo-700', icon: <MdLocalShipping /> },
    'QC Pending': { color: 'bg-amber-100 text-amber-700', icon: <FaClipboardCheck /> },
    'QC Passed': { color: 'bg-emerald-100 text-emerald-700', icon: <FaCheckCircle /> },
    'QC Failed': { color: 'bg-red-100 text-red-700', icon: <FaTimesCircle /> },
    'Refund Initiated': { color: 'bg-orange-100 text-orange-700', icon: <FaMoneyBillWave /> },
    'Refund Completed': { color: 'bg-green-100 text-green-700', icon: <FaCheckCircle /> },
    'Rejected': { color: 'bg-red-100 text-red-700', icon: <FaTimesCircle /> },
    'Closed': { color: 'bg-gray-100 text-gray-700', icon: <FaCheckCircle /> }
};

const RETURN_STATUSES = [
    'All', 'Requested', 'Approved', 'Pickup Scheduled', 'Picked Up',
    'QC Pending', 'QC Passed', 'QC Failed', 'Refund Initiated',
    'Refund Completed', 'Rejected', 'Closed'
];

const ReturnManagement = () => {
    const navigate = useNavigate();
    const [returns, setReturns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeStatus, setActiveStatus] = useState('All');
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [statusCounts, setStatusCounts] = useState({});
    const [search, setSearch] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [selectedReturn, setSelectedReturn] = useState(null);
    const [showActionModal, setShowActionModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [actionNotes, setActionNotes] = useState('');
    const [targetStatus, setTargetStatus] = useState('');
    const [qcDecision, setQcDecision] = useState('');
    const [refundMethod, setRefundMethod] = useState('original_payment');
    const [refundAmount, setRefundAmount] = useState('');
    const [refundTransactionId, setRefundTransactionId] = useState('');
    const [pickupDate, setPickupDate] = useState(null);
    const searchTimeoutRef = useRef(null);
    const PAGE_SIZE = 12;

    const fetchReturns = useCallback(async (currentPage = 1) => {
        try {
            setLoading(true);
            const payload = {
                page: currentPage,
                limit: PAGE_SIZE,
                status: activeStatus !== 'All' ? activeStatus : undefined,
                search: search || undefined,
                startDate: startDate?.toISOString(),
                endDate: endDate?.toISOString()
            };
            const response = await getReturnRequests(payload);
            if (response?.success) {
                if (currentPage === 1) {
                    setReturns(response.data || []);
                } else {
                    setReturns(prev => [...prev, ...(response.data || [])]);
                }
                setTotalCount(response.totalCount || 0);
                setStatusCounts(response.statusCounts || {});
            }
        } catch (error) {
            toast.error('Failed to load return requests');
        } finally {
            setLoading(false);
        }
    }, [activeStatus, search, startDate, endDate]);

    useEffect(() => {
        setPage(1);
        fetchReturns(1);
    }, [activeStatus, fetchReturns]);

    const handleSearch = (value) => {
        setSearch(value);
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setPage(1);
            fetchReturns(1);
        }, 500);
    };

    const handleScroll = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop - clientHeight < 100 && !loading && returns.length < totalCount) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchReturns(nextPage);
        }
    }, [loading, returns.length, totalCount, page, fetchReturns]);

    const handleAction = (returnItem, status) => {
        setSelectedReturn(returnItem);
        setTargetStatus(status);
        setActionNotes('');
        setQcDecision('');
        setRefundAmount(returnItem.refundAmount || '');
        setRefundTransactionId('');
        setPickupDate(null);
        setShowActionModal(true);
    };

    const submitAction = async () => {
        if (!selectedReturn || !targetStatus) return;
        setActionLoading(true);
        try {
            const payload = {
                returnId: selectedReturn._id,
                newStatus: targetStatus,
                notes: actionNotes
            };

            if (targetStatus === 'QC Passed' || targetStatus === 'QC Failed') {
                payload.qcResult = targetStatus === 'QC Passed' ? 'passed' : 'failed';
                payload.qcDecision = qcDecision || undefined;
            }
            if (targetStatus === 'Refund Initiated') {
                payload.refundMethod = refundMethod;
                payload.refundAmount = parseFloat(refundAmount) || selectedReturn.refundAmount;
            }
            if (targetStatus === 'Refund Completed') {
                payload.refundTransactionId = refundTransactionId;
            }
            if (targetStatus === 'Pickup Scheduled') {
                payload.pickupDate = pickupDate?.toISOString();
                payload.pickupNotes = actionNotes;
            }

            const response = await updateReturnStatus(payload);
            if (response?.success) {
                toast.success(`Return ${targetStatus.toLowerCase()} successfully`);
                setShowActionModal(false);
                fetchReturns(1);
            } else {
                toast.error(response?.message || 'Action failed');
            }
        } catch (error) {
            toast.error('Failed to update return status');
        } finally {
            setActionLoading(false);
        }
    };

    const getNextActions = (currentStatus) => {
        const transitions = {
            'Requested': ['Approved', 'Rejected'],
            'Approved': ['Pickup Scheduled'],
            'Pickup Scheduled': ['Picked Up'],
            'Picked Up': ['QC Pending', 'QC Passed', 'QC Failed'],
            'QC Pending': ['QC Passed', 'QC Failed'],
            'QC Passed': ['Refund Initiated'],
            'QC Failed': ['Refund Initiated', 'Closed'],
            'Refund Initiated': ['Refund Completed'],
            'Refund Completed': ['Closed']
        };
        return transitions[currentStatus] || [];
    };

    const getStatusBadge = (status) => {
        const config = RETURN_STATUS_CONFIG[status];
        return config ? config.color : 'bg-gray-100 text-gray-700';
    };

    return (
        <div className="h-screen flex flex-col overflow-hidden bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <FaUndoAlt className="text-orange-600" />
                            Return & Refund Management
                        </h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Manage return requests, QC, and refund approvals
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all text-sm font-medium"
                        >
                            <FaFilter />
                            Filters
                        </button>
                        <button
                            onClick={() => { setPage(1); fetchReturns(1); }}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-xl hover:bg-indigo-100 transition-all text-sm font-medium"
                        >
                            <HiOutlineRefresh />
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Search & Filters */}
            <div className="bg-white border-b border-gray-200 px-6 py-3">
                <div className="flex items-center gap-3">
                    <div className="relative flex-1 max-w-md">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by return ID, order ID, or customer..."
                            value={search}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                        />
                    </div>
                    {showFilters && (
                        <div className="flex items-center gap-2">
                            <DatePicker
                                selected={startDate}
                                onChange={(date) => setStartDate(date)}
                                placeholderText="Start Date"
                                className="px-3 py-2 border border-gray-200 rounded-xl text-sm w-32"
                            />
                            <span className="text-gray-400">to</span>
                            <DatePicker
                                selected={endDate}
                                onChange={(date) => setEndDate(date)}
                                placeholderText="End Date"
                                className="px-3 py-2 border border-gray-200 rounded-xl text-sm w-32"
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Status Tabs */}
            <div className="bg-white border-b border-gray-200 px-6">
                <div className="flex gap-1 overflow-x-auto scrollbar-thin py-1">
                    {RETURN_STATUSES.map((status) => (
                        <button
                            key={status}
                            onClick={() => setActiveStatus(status)}
                            className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium whitespace-nowrap rounded-lg transition-all ${
                                activeStatus === status
                                    ? 'bg-indigo-100 text-indigo-700'
                                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                            }`}
                        >
                            {status}
                            {statusCounts[status] > 0 && (
                                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                                    activeStatus === status ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-600'
                                }`}>
                                    {statusCounts[status]}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Returns List */}
            <div className="flex-1 min-h-0 px-6 py-4 overflow-y-auto" onScroll={handleScroll}>
                {loading && returns.length === 0 ? (
                    <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="bg-white rounded-xl p-6 border border-gray-100 animate-pulse">
                                <div className="h-4 bg-gray-200 rounded w-1/4 mb-3"></div>
                                <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                                <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                            </div>
                        ))}
                    </div>
                ) : returns.length === 0 ? (
                    <div className="text-center py-16">
                        <FaCheckCircle className="mx-auto text-green-400 text-5xl mb-4" />
                        <h3 className="text-lg font-semibold text-gray-700">No Returns Found</h3>
                        <p className="text-gray-500 mt-1">No return requests match your filters.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {returns.map((ret) => (
                            <motion.div
                                key={ret._id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-white rounded-xl p-5 border border-gray-100 hover:shadow-md transition-all"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="font-semibold text-gray-900">{ret.returnId}</h3>
                                            <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${getStatusBadge(ret.status)}`}>
                                                {ret.status}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-500">
                                            Order: <span className="text-indigo-600 font-medium cursor-pointer hover:underline"
                                                onClick={() => navigate(`/orderOverview/${ret.orderId}`)}>{ret.saleId}</span>
                                            {' '}• {ret.customerName}
                                        </p>
                                        <p className="text-sm text-gray-500 mt-0.5">
                                            {ret.items?.length || 0} items • Refund: ₹{(ret.refundAmount || 0).toLocaleString()}
                                            {' '}• {new Date(ret.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                        </p>
                                        <p className="text-sm text-gray-600 mt-1">
                                            <span className="font-medium">Reason:</span> {ret.reason}
                                        </p>

                                        {/* Item thumbnails */}
                                        <div className="flex gap-2 mt-2">
                                            {(ret.items || []).slice(0, 4).map((item, idx) => (
                                                <div key={idx} className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-lg">
                                                    {item.images?.preview && (
                                                        <img src={item.images.preview} alt="" className="w-6 h-6 rounded object-cover" />
                                                    )}
                                                    <span className="text-xs text-gray-600 max-w-[100px] truncate">{item.productName}</span>
                                                    <span className="text-xs text-gray-400">×{item.quantity}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex flex-col items-end gap-2 ml-4">
                                        {getNextActions(ret.status).map((action) => (
                                            <button
                                                key={action}
                                                onClick={() => handleAction(ret, action)}
                                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                                    action === 'Rejected' || action === 'QC Failed'
                                                        ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                                                        : action === 'Approved' || action === 'QC Passed' || action === 'Refund Completed'
                                                        ? 'bg-green-50 text-green-600 hover:bg-green-100 border border-green-200'
                                                        : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-200'
                                                }`}
                                            >
                                                {action}
                                            </button>
                                        ))}
                                        <button
                                            onClick={() => navigate(`/orderOverview/${ret.orderId}`)}
                                            className="flex items-center gap-1 text-xs text-gray-500 hover:text-indigo-600"
                                        >
                                            <FaEye /> View Order
                                        </button>

                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>

            {/* Action Modal */}
            <AnimatePresence>
                {showActionModal && selectedReturn && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                        onClick={() => setShowActionModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Modal Header */}
                            <div className="px-6 py-4 border-b border-gray-100">
                                <h3 className="text-lg font-bold text-gray-900">
                                    {targetStatus === 'Rejected' ? 'Reject Return' :
                                     targetStatus === 'Approved' ? 'Approve Return' :
                                     `Update to: ${targetStatus}`}
                                </h3>
                                <p className="text-sm text-gray-500">
                                    {selectedReturn.returnId} • {selectedReturn.customerName}
                                </p>
                            </div>

                            {/* Modal Content */}
                            <div className="px-6 py-4 space-y-4">
                                {/* Notes */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                    <textarea
                                        value={actionNotes}
                                        onChange={(e) => setActionNotes(e.target.value)}
                                        placeholder="Add notes about this action..."
                                        rows={3}
                                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 resize-none"
                                    />
                                </div>

                                {/* QC Decision (for QC Passed/Failed) */}
                                {(targetStatus === 'QC Passed' || targetStatus === 'QC Failed') && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Item Decision</label>
                                        <div className="flex gap-2">
                                            {['resale', 'refurbish', 'discard'].map((decision) => (
                                                <button
                                                    key={decision}
                                                    onClick={() => setQcDecision(decision)}
                                                    className={`px-4 py-2 rounded-xl text-sm font-medium capitalize transition-all ${
                                                        qcDecision === decision
                                                            ? 'bg-indigo-600 text-white'
                                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                    }`}
                                                >
                                                    {decision}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Refund fields */}
                                {targetStatus === 'Refund Initiated' && (
                                    <>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Refund Method</label>
                                            <select
                                                value={refundMethod}
                                                onChange={(e) => setRefundMethod(e.target.value)}
                                                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                                            >
                                                <option value="original_payment">Original Payment Method</option>
                                                <option value="store_credit">Store Credit</option>
                                                <option value="bank_transfer">Bank Transfer</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Refund Amount (₹)</label>
                                            <input
                                                type="number"
                                                value={refundAmount}
                                                onChange={(e) => setRefundAmount(e.target.value)}
                                                placeholder={selectedReturn.refundAmount}
                                                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                                            />
                                        </div>
                                    </>
                                )}

                                {/* Refund Transaction ID */}
                                {targetStatus === 'Refund Completed' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Transaction ID</label>
                                        <input
                                            type="text"
                                            value={refundTransactionId}
                                            onChange={(e) => setRefundTransactionId(e.target.value)}
                                            placeholder="Enter refund transaction reference"
                                            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                                        />
                                    </div>
                                )}

                                {/* Pickup Date */}
                                {targetStatus === 'Pickup Scheduled' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Date</label>
                                        <DatePicker
                                            selected={pickupDate}
                                            onChange={(date) => setPickupDate(date)}
                                            minDate={new Date()}
                                            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                                            placeholderText="Select pickup date"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Modal Actions */}
                            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
                                <button
                                    onClick={() => setShowActionModal(false)}
                                    className="flex-1 py-2.5 px-4 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={submitAction}
                                    disabled={actionLoading}
                                    className={`flex-1 py-2.5 px-4 rounded-xl text-white text-sm font-medium transition-all ${
                                        targetStatus === 'Rejected' || targetStatus === 'QC Failed'
                                            ? 'bg-red-600 hover:bg-red-700'
                                            : 'bg-indigo-600 hover:bg-indigo-700'
                                    } disabled:opacity-50`}
                                >
                                    {actionLoading ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                            Processing...
                                        </span>
                                    ) : (
                                        `Confirm ${targetStatus}`
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ReturnManagement;
